import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, ArrowRight, BookOpen, FileText } from 'lucide-react';
import DisplaySynthesisRender from './DisplaySynthesisRender';

const DisplaySynthesis = ({ 
  synthesis, 
  searchQuery, 
  documents, 
  onViewDocumentPassage, 
  onSelectConcept 
}) => {
  const [expanded, setExpanded] = useState(false);
  const [conceptsExpanded, setConceptsExpanded] = useState(true);
  const [processedSummary, setProcessedSummary] = useState("");

  // Adapter les concepts à la structure de DocumentAnalysis
  const concepts = Array.isArray(synthesis?.mainConcepts) 
    ? synthesis.mainConcepts.filter(Boolean).map(conceptItem => {
        if (typeof conceptItem === 'string') {
          const match = conceptItem.match(/(.+?)\s*\(page\s*(\d+|N\/A)\)/i);
          if (match) {
            return {
              concept: match[1].trim(),
              pages: match[2] !== 'N/A' ? match[2] : null
            };
          }
          return { concept: conceptItem };
        }
        return conceptItem;
      })
    : [];

  // Extraire les références de documents
  const documentRefs = Array.isArray(synthesis?.documentReferences) 
    ? synthesis.documentReferences
    : [];

  // Traiter le texte de synthèse
  useEffect(() => {
    if (!synthesis || !synthesis.summary) {
      setProcessedSummary("Aucun résumé disponible pour cette recherche.");
      return;
    }

    console.log("Traitement de la synthèse...");
    console.log("Références de documents disponibles:", documentRefs.length);
    
    let summaryText = synthesis.summary;
    
    // 1. Améliorer la mise en forme des paragraphes
    if (!summaryText.includes('\n\n')) {
      // Identifier les points logiques pour diviser en paragraphes
      const paragraphs = [];
      let currentParagraph = "";
      
      // Diviser par phrases
      const sentences = summaryText.split(/(?<=\.\s)/);
      
      sentences.forEach((sentence, index) => {
        currentParagraph += sentence;
        
        // Créer un nouveau paragraphe après environ 2-3 phrases ou aux marqueurs logiques
        if ((index + 1) % 3 === 0 || 
            sentence.includes("En conclusion") || 
            sentence.includes("Par ailleurs") ||
            sentence.includes("De plus") ||
            sentence.includes("Cependant") ||
            sentence.includes("Néanmoins") ||
            sentence.includes("En résumé") ||
            sentence.includes("Ainsi") ||
            sentence.includes("Toutefois") ||
            sentence.includes("Enfin")) {
          paragraphs.push(currentParagraph.trim());
          currentParagraph = "";
        }
      });
      
      // Ajouter le dernier paragraphe s'il reste du texte
      if (currentParagraph.trim()) {
        paragraphs.push(currentParagraph.trim());
      }
      
      summaryText = paragraphs.join('\n\n');
    }
    
    // 2. Ajouter les références aux documents pertinents
    if (documentRefs && documentRefs.length > 0) {
      let documentCounter = 1;
      const linkedDocs = new Set();
      
      documentRefs.forEach(doc => {
        if (!doc.title || linkedDocs.has(doc.id)) return;
        
        // Créer la référence numérotée
        const docRef = `[[${documentCounter}]](${doc.id})`;
        documentCounter++;
        
        let docLinked = false;
        
        // Si le document a des extraits, chercher des correspondances dans le résumé
        if (Array.isArray(doc.extraits) && doc.extraits.length > 0) {
          for (const extrait of doc.extraits) {
            const extractText = typeof extrait === 'string' 
              ? extrait 
              : extrait.content || '';
            
            if (extractText.length < 20) continue;
            
            // Extraire des termes significatifs de l'extrait
            const significantTerms = extractText
              .split(/\s+/)
              .filter(term => term.length > 5)
              .map(term => term.replace(/[.,;:!?()'"]/g, ''))
              .filter(Boolean)
              .slice(0, 5);
            
            // Chercher ces termes dans le résumé
            for (const term of significantTerms) {
              const position = summaryText.toLowerCase().indexOf(term.toLowerCase());
              if (position !== -1) {
                // Trouver la fin de la phrase actuelle
                let endOfSentence = summaryText.indexOf('. ', position);
                if (endOfSentence === -1) endOfSentence = summaryText.length;
                else endOfSentence += 1; // inclure le point
                
                // Vérifier si une référence existe déjà à cet endroit
                const sentenceEnd = summaryText.substring(endOfSentence - 20, endOfSentence + 5);
                if (!sentenceEnd.includes('[[')) {
                  // Insérer la référence
                  summaryText = 
                    summaryText.substring(0, endOfSentence) + 
                    ' ' + docRef + ' ' +
                    summaryText.substring(endOfSentence);
                  
                  docLinked = true;
                  linkedDocs.add(doc.id);
                  break;
                }
              }
            }
            
            if (docLinked) break;
          }
        }
        
        // Si aucune référence n'a été insérée, l'ajouter à la fin d'un paragraphe pertinent
        if (!docLinked) {
          // Trouver un endroit approprié à la fin d'un paragraphe
          const paraMatches = [...summaryText.matchAll(/\.\s*\n\n/g)];
          if (paraMatches.length > 0) {
            // Insérer à la fin d'un paragraphe (dernier pour simplifier)
            const matchPos = paraMatches[paraMatches.length - 1].index;
            summaryText = 
              summaryText.substring(0, matchPos + 1) + 
              ' ' + docRef + ' ' +
              summaryText.substring(matchPos + 1);
          } else {
            // En dernier recours, ajouter à la fin du texte
            summaryText = summaryText.trim() + ' ' + docRef;
          }
          
          linkedDocs.add(doc.id);
        }
      });
    }
    
    console.log("Synthèse transformée, longueur:", summaryText.length);
    setProcessedSummary(summaryText);
  }, [synthesis, documentRefs]);

  // Si la synthèse est invalide, n'affichez rien
  if (!synthesis || typeof synthesis !== 'object') {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm mb-6 overflow-hidden">
      {/* Entête de la synthèse */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Synthèse de votre recherche
            </h3>
            <p className="text-sm text-gray-500">
              {searchQuery ? `"${searchQuery}"` : 'Résultats de recherche'}
            </p>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label={expanded ? 'Réduire la synthèse' : 'Voir la synthèse complète'}
          >
            {expanded ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
      
      {/* Contenu de la synthèse */}
      <div className={`transition-all duration-300 ease-in-out ${expanded ? 'max-h-[2000px]' : 'max-h-48'} overflow-hidden`}>
        {/* Instructions pour l'utilisateur */}
        <div className="px-4 pt-3 text-xs text-blue-600 flex items-center bg-blue-50 py-1 mx-4 mt-3 rounded">
          <FileText className="h-3 w-3 mr-1" />
          <span>Le texte souligné est cliquable et vous dirige vers les passages correspondants dans les documents.</span>
        </div>
        
        {/* Résumé principal avec zones cliquables */}
        <div className="p-4 pt-2">
          <DisplaySynthesisRender 
            content={processedSummary} 
            className="text-gray-800 leading-relaxed"
            documentRefs={documentRefs}
            onPassageClick={onViewDocumentPassage}
          />
          
          {/* Légende des sources */}
          {documentRefs.length > 0 && (
            <div className="mt-4 text-xs text-gray-500 border-t border-gray-100 pt-3">
              <div className="mb-2 font-medium text-gray-700">Sources citées:</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {documentRefs.map((doc, index) => (
                  <div 
                    key={doc.id || index} 
                    className="flex items-center cursor-pointer hover:text-blue-600 group"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      
                      setTimeout(() => {
                        const passage = {
                          content: Array.isArray(doc.extraits) && doc.extraits.length > 0 
                            ? (typeof doc.extraits[0] === 'string' ? doc.extraits[0] : doc.extraits[0]?.content || "") 
                            : "",
                          pageNumber: Array.isArray(doc.relevantPages) && doc.relevantPages.length > 0
                            ? doc.relevantPages[0] : 1
                        };
                        onViewDocumentPassage && onViewDocumentPassage(doc.id, passage);
                      }, 0);
                    }}
                  >
                    <span className="inline-flex items-center justify-center bg-blue-50 text-blue-600 rounded-full w-5 h-5 text-xs font-medium mr-1 flex-shrink-0">
                      {index+1}
                    </span>
                    <span className="truncate">{doc.title}</span>
                    <span className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <FileText className="h-3 w-3 text-blue-500" />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Section des concepts clés */}
        {concepts.length > 0 && (
          <div className="p-4 bg-gray-50 border-t border-gray-100">
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setConceptsExpanded(!conceptsExpanded)}
            >
              <h4 className="text-md font-medium text-gray-900 flex items-center">
                <BookOpen className="h-4 w-4 mr-2 text-blue-500" />
                Concepts clés identifiés ({concepts.length})
              </h4>
              {conceptsExpanded ? (
                <ChevronUp className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              )}
            </div>
            
            {conceptsExpanded && (
              <div className="mt-3 grid gap-3 grid-cols-1 md:grid-cols-2">
                {concepts.map((conceptItem, index) => {
                  const conceptId = conceptItem.id || `concept-${index}`;
                  const conceptName = conceptItem.concept || 'Concept sans nom';
                  
                  // Déterminer le numéro de page
                  const pageInfo = (() => {
                    if (conceptItem.pageNumber) return conceptItem.pageNumber;
                    if (conceptItem.pages) {
                      if (typeof conceptItem.pages === 'string') {
                        const match = conceptItem.pages.match(/\d+/);
                        return match ? match[0] : null;
                      }
                      if (Array.isArray(conceptItem.pages) && conceptItem.pages.length > 0) {
                        return conceptItem.pages[0];
                      }
                    }
                    return null;
                  })();
                  
                  // Calculer la couleur selon la pertinence
                  const getBorderColor = () => {
                    if (!conceptItem.relevance) return 'border-gray-200';
                    const relevanceScore = conceptItem.relevance;
                    if (relevanceScore > 0.8) return 'border-blue-300';
                    if (relevanceScore > 0.6) return 'border-blue-200';
                    return 'border-gray-200';
                  };
                  
                  return (
                    <div 
                      key={conceptId} 
                      className={`bg-white rounded-lg p-3 border ${getBorderColor()} hover:shadow-sm transition-shadow`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div 
                            className="text-blue-600 font-medium cursor-pointer hover:underline mb-1"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setTimeout(() => {
                                onSelectConcept && onSelectConcept(conceptItem);
                              }, 0);
                            }}
                          >
                            {conceptName}
                            {pageInfo && (
                              <span className="text-sm text-gray-500 ml-2">
                                (page {pageInfo})
                              </span>
                            )}
                          </div>
                          
                          {(conceptItem.description || conceptItem.resume) && (
                            <p className="text-sm text-gray-700 mb-2">{conceptItem.description || conceptItem.resume}</p>
                          )}
                          
                          {conceptItem.extraits && (
                            <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded italic border-l-2 border-blue-200">
                              {typeof conceptItem.extraits === 'string' 
                                ? conceptItem.extraits 
                                : Array.isArray(conceptItem.extraits) 
                                  ? conceptItem.extraits.join(" [...] ") 
                                  : null}
                            </div>
                          )}
                          
                          {conceptItem.documentId && (
                            <div 
                              className="mt-2 text-xs flex items-center text-gray-500 hover:text-blue-600 cursor-pointer"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setTimeout(() => {
                                  onViewDocumentPassage && onViewDocumentPassage(conceptItem.documentId, {
                                    conceptName: conceptName,
                                    pageNumber: pageInfo || 1,
                                    content: conceptItem.extraits || ""
                                  });
                                }, 0);
                              }}
                            >
                              <FileText className="h-3.5 w-3.5 mr-1" />
                              <span className="truncate">
                                {conceptItem.documentTitle || 'Voir dans le document'}
                              </span>
                              <ArrowRight className="h-3 w-3 ml-1" />
                            </div>
                          )}
                          
                          {conceptItem.relevance && (
                            <div className="mt-2 flex items-center">
                              <div className="h-1.5 flex-1 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full ${
                                    conceptItem.relevance > 0.8 ? 'bg-blue-600' : 
                                    conceptItem.relevance > 0.6 ? 'bg-blue-500' : 
                                    conceptItem.relevance > 0.4 ? 'bg-blue-400' : 'bg-blue-300'
                                  }`}
                                  style={{ width: `${Math.min(conceptItem.relevance * 100, 100)}%` }}
                                />
                              </div>
                              <span className="ml-2 text-xs text-gray-500">
                                {Math.round(conceptItem.relevance * 100)}%
                              </span>
                            </div>
                          )}
                        </div>
                        
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setTimeout(() => {
                              onSelectConcept && onSelectConcept(conceptItem);
                            }, 0);
                          }}
                          className="ml-2 p-1 text-gray-400 hover:text-blue-600 bg-white rounded-full hover:shadow-sm"
                          title="Voir les détails du concept"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Bouton d'expansion si nécessaire */}
      {!expanded && (
        <div className="p-2 text-center border-t border-gray-100">
          <button
            onClick={() => setExpanded(true)}
            className="text-blue-600 text-sm hover:text-blue-700 focus:outline-none flex items-center justify-center mx-auto"
          >
            <span>Voir plus de détails</span>
            <ChevronDown className="h-4 w-4 ml-1" />
          </button>
        </div>
      )}
    </div>
  );
};

export default DisplaySynthesis;